import { dateTime as dateTimeHelper, mediaUrl } from '../utils/helpers';
import { getClientTz } from '../utils/date';
import pluralize from 'pluralize';
import { vxFormatCount, vxFormatPercentage } from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs';

const entryPointsList = {
    api: 'API',
    automated_upload: 'Automated File Upload',
    connect: 'Contact Collector',
    conversion: 'Conversion',
    hubspot: 'HubSpot',
    ibt: 'Inbound Text',
    integration: 'Integration',
    manual_upload: 'Manual File Upload',
    mparticle: 'mParticle',
    salesforce: 'Salesforce',
    shopify: 'Shopify',
    stt: 'Shift to Text',
    user: 'Manual Creation',
    zipwhip_import: 'Zipwhip Import',
};

const externalListVendorsList = {
    amplitude: 'Amplitude',
    hubspot: 'HubSpot',
    mparticle: 'mParticle',
};

export function weekDayOfMonth(value) {
    let tz = getClientTz();

    return dayjs.utc(value).tz(tz).format('dddd, MMMM Do');
}

export function timeAmPm(value) {
    const tz = getClientTz();
    const timezone = dayjs.utc(value).tz(tz).format('z');

    return (
        dayjs.utc(value).tz(tz).format('h:mm a') +
        ' ' +
        (timezone.startsWith('+') || timezone.startsWith('-') ? 'GMT' + timezone : timezone)
    );
}

export function formatCampaignMessageDelay(message) {
    return dayjs.duration(message.delay_amount, message.delay_type).humanize();
}

export function mediaUrlFilter(path) {
    return mediaUrl(path);
}

export function displayContactName(contact, showDeleted = false) {
    if (contact == null) {
        return 'Anonymous';
    }

    if (contact.first_name && contact.last_name) {
        return contact.first_name + ' ' + contact.last_name;
    }

    if (contact.first_name) {
        return contact.first_name;
    }

    if (contact.last_name) {
        return contact.last_name;
    }

    if (contact.phone) {
        return formatPhoneNumber(contact.phone);
    }
    return !contact.phone && showDeleted ? 'Deleted Contact' : 'Anonymous';
}

/**
 * Display the contact name or phone number.
 *
 * @param {Object} contact
 * @return {String}
 */
export function contactName(contact, showDeleted = false) {
    return displayContactName(contact, showDeleted);
}

/**
 * Display the contact phone if they have a first or last name.
 *
 * @param {Object} contact
 * @return {String}
 */
export function contactSubtitle(contact) {
    if ((contact.first_name || contact.last_name) && contact.phone) {
        return formatPhoneNumber(contact.phone);
    }

    return '';
}

/**
 * Display readable date in the user timezone.
 *
 * @param {String} value
 * @return {String}
 */
export function date(value) {
    if (!value) {
        return 'never';
    }

    let timeZone = getClientTz();

    return dayjs.utc(value).tz(timeZone).format('MMM Do, YYYY');
}

/**
 * Display readable date and time in the user timezone.
 *
 * @param {String} value
 * @return {String}
 */
export function dateTime(value) {
    return dateTimeHelper(value);
}

export function formatCount(number) {
    return vxFormatCount(number);
}

export function formatPercentage(percentage, symbol = true) {
    return vxFormatPercentage(percentage, symbol);
}

/**
 * Display a short version of the given date.
 *
 * @param {String} value
 * @return {String}
 */
export function shortDate(value) {
    let timeZone = getClientTz();
    let dateTime = dayjs.utc(value).tz(timeZone);

    return dateTime.isSame(dayjs(), 'day') ? dateTime.format('h:mm A') : dateTime.format('MM/DD/YY');
}

/**
 * Display a short version of the given date.
 *
 * @param {String} value
 * @return {String}
 */
export function formatMessageTime(value) {
    if (value) {
        const timeZone = getClientTz();
        const dateTime = dayjs.utc(value).tz(timeZone);
        const format = dateTime.isSame(dayjs(), 'day') ? 'h:mm A' : 'M/D/YY h:mm A';

        return dateTime.format(format);
    }

    return '';
}

/**
 * Format the phone number using national phone number format.
 *
 * @param {String} srcPhone It can be e.g. '+12133734253'
 * @return {String} E.g. '(213) 373-4253'
 */
export function formatPhoneNumber(srcPhone) {
    const cleanedPhone = srcPhone.replace(/\D/g, '');

    if (cleanedPhone.length === 10) {
        return `(${cleanedPhone.substring(0, 3)}) ${cleanedPhone.substring(3, 6)}-${cleanedPhone.substring(6, 10)}`;
    }

    if (cleanedPhone.charAt(0) === '1' && cleanedPhone.length === 11) {
        return `(${cleanedPhone.substring(1, 4)}) ${cleanedPhone.substring(4, 7)}-${cleanedPhone.substring(7, 11)}`;
    }

    return srcPhone;
}

/**
 * Pluralize or singularize a word based on the passed in count.
 *
 * @param  {string}  word      The word to pluralize
 * @param  {number}  count     How many of the word exist
 * @param  {boolean} inclusive Whether to prefix with the number (e.g. 3 ducks)
 * @return {string}
 */
export const pluralized = (word, count, inclusive) => {
    if (inclusive) {
        const formattedCount = Intl.NumberFormat('en-US').format(count);

        return `${formattedCount} ${pluralize(word, count)}`;
    }

    return pluralize(word, count);
};

/**
 * Display the contact entry point.
 *
 * @param {String|null} entryPoint
 * @return {String}
 */
export function entryPoint(entryPoint) {
    return entryPoint ? entryPointsList[entryPoint] ?? 'Unknown' : 'Legacy';
}

/**
 * Get entry points for Select components.
 *
 * @returns {{value: string, label: string}[]}
 */
export function entryPointsForSelect({ includeLegacy = false } = {}) {
    const list = { ...entryPointsList };

    if (includeLegacy) {
        list.legacy = 'Legacy';
    }

    return Object.entries(list)
        .sort(([, valueA], [, valueB]) => valueA.localeCompare(valueB))
        .map(([key, value]) => {
            return {
                value: key,
                label: value,
            };
        });
}

/**
 * Display the external list vendor.
 *
 * @param {String|null} vendor
 * @return {String}
 */
export function externalListVendor(vendor) {
    return vendor ? externalListVendorsList[vendor] ?? 'Unknown' : 'Unknown';
}

/**
 * Get external list vendors for Select components.
 *
 * @returns {{value: string, label: string}[]}
 */
export function externalListVendorsForSelect() {
    return Object.entries(externalListVendorsList)
        .sort(([, valueA], [, valueB]) => valueA.localeCompare(valueB))
        .map(([key, value]) => ({
            value: key,
            label: value,
        }));
}
